import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bars3Icon, XMarkIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { changeLanguage } from '../../i18n';
import { Container, PillLink } from './primitives';

export const NAV_SECTIONS = ['platform', 'clients', 'projects', 'finance'];

export function LanguageToggle({ className = '', tone = 'dark' }) {
  const { t, i18n } = useTranslation();
  const next = i18n.language === 'ar' ? 'en' : 'ar';
  const toneClasses = tone === 'light' ? 'text-white/70 hover:text-white' : 'text-ink/70 hover:text-ink';

  return (
    <button
      type="button"
      onClick={() => changeLanguage(next)}
      className={`lp-focus inline-flex h-10 items-center gap-1.5 rounded-full px-2 text-[15px] transition-colors ${toneClasses} ${className}`}
      aria-label={t('landing.nav.switchLanguage')}
    >
      <GlobeAltIcon className="h-[18px] w-[18px]" aria-hidden="true" />
      <span lang={next}>{next === 'ar' ? 'العربية' : 'English'}</span>
    </button>
  );
}

export default function LandingNav() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const toggleRef = useRef(null);
  const firstLinkRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    firstLinkRef.current?.focus();

    const onKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onResize = () => {
      if (window.innerWidth >= 1024) setOpen(false);
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const scrollToTop = (event) => {
    if (window.location.pathname === '/') {
      event.preventDefault();
      setOpen(false);
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 bg-white transition-shadow duration-200 ${
        scrolled || open ? 'shadow-[0_1px_0_rgba(0,0,0,0.06)]' : ''
      }`}
    >
      <Container>
        <nav className="flex h-[72px] items-center justify-between gap-4" aria-label={t('landing.nav.label')}>
          <div className="flex items-center gap-8 xl:gap-10">
            <Link to="/" onClick={scrollToTop} className="lp-focus -m-1 shrink-0 rounded-md p-1" aria-label={t('landing.nav.home')}>
              <img src="/proctm_logo_blue.png" alt="ProCTM" width="500" height="300" className="-mx-2 h-14 w-auto" />
            </Link>
            <ul className="hidden items-center gap-1 lg:flex">
              {NAV_SECTIONS.map((id) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    className="lp-focus rounded-md px-3 py-2 text-[15px] text-ink/80 transition-colors hover:text-ink"
                  >
                    {t(`landing.nav.${id}`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <LanguageToggle className="hidden lg:inline-flex" />
            <Link
              to="/login"
              className="lp-focus hidden rounded-md px-3 py-2 text-[15px] text-ink/80 transition-colors hover:text-ink lg:inline-flex"
            >
              {t('landing.nav.signIn')}
            </Link>
            <PillLink to="/register" size="sm" className="ms-1 max-[359px]:hidden">
              {t('landing.cta.getStarted')}
            </PillLink>
            <button
              ref={toggleRef}
              type="button"
              className="lp-focus -me-2 inline-flex h-11 w-11 items-center justify-center rounded-full text-ink lg:hidden"
              aria-expanded={open}
              aria-controls="landing-mobile-menu"
              aria-label={open ? t('landing.nav.closeMenu') : t('landing.nav.openMenu')}
              onClick={() => setOpen((value) => !value)}
            >
              {open ? <XMarkIcon className="h-6 w-6" aria-hidden="true" /> : <Bars3Icon className="h-6 w-6" aria-hidden="true" />}
            </button>
          </div>
        </nav>
      </Container>

      {open && (
        <div
          id="landing-mobile-menu"
          className="fixed inset-x-0 bottom-0 top-[72px] z-40 overflow-y-auto bg-white lg:hidden"
        >
          <Container className="flex min-h-full flex-col py-6">
            <ul className="flex flex-col items-center gap-1">
              {NAV_SECTIONS.map((id, index) => (
                <li key={id} className="w-full">
                  <a
                    ref={index === 0 ? firstLinkRef : undefined}
                    href={`#${id}`}
                    onClick={() => setOpen(false)}
                    className="lp-focus block rounded-md py-3 text-center text-[17px] text-ink"
                  >
                    {t(`landing.nav.${id}`)}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-col gap-3">
              <PillLink to="/login" variant="outline" className="w-full" onClick={() => setOpen(false)}>
                {t('landing.nav.signIn')}
              </PillLink>
              <PillLink to="/register" className="w-full" onClick={() => setOpen(false)}>
                {t('landing.cta.getStarted')}
              </PillLink>
            </div>
            <div className="mt-6 flex justify-center">
              <LanguageToggle />
            </div>
          </Container>
        </div>
      )}
    </header>
  );
}
